import { FC, memo, useEffect, useState } from 'react';
import { Col, Image, Row, Button } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { getError } from '../../services/OryService';
import errorImage from 'assets/images/spaceman.svg';
import { ErrorGlobal } from 'interfaces';
import { routes } from '../../models/routes';

import styles from './Errors.module.scss';

// Ory reports server-side failures with the internal endpoint that failed, e.g.
// "POST http://api-main:4000/dg/nir/verify giving up after 3 attempt(s)". That names
// an internal host and route, so it must never reach the browser. 5xx reasons are of
// no use to the user anyway; the detail stays in the API logs.
const GENERIC_SERVER_ERROR = 'Something went wrong on our side. Please try again later or contact support.';

const isServerError = (code?: number) => code !== undefined && code >= 500;

// Hydra appends its internal debug detail to error_description, e.g. the Postgres host,
// port, user and database behind a failed connection. Only the OAuth error code is
// rendered, mapped to copy that names nothing internal.
const OAUTH_ERRORS: Record<string, string> = {
  access_denied: 'You cancelled the sign-in request, or it was denied.',
  invalid_request: 'The sign-in request was malformed. Please start again from the application you came from.',
  login_required: 'Your session has expired. Please sign in again.',
  consent_required: 'This application needs your consent before it can continue.',
};

const oauthMessage = (code: string) => OAUTH_ERRORS[code] ?? GENERIC_SERVER_ERROR;

export const Errors: FC = memo(() => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const id = params.get('id');
  const oauthError = params.get('error');
  const referrer = document.referrer;

  const [error, setError] = useState<ErrorGlobal>();

  useEffect(() => {
    if (id) {
      getError(id).then(({ data }) => {
        [401, 404].includes(data.error.code) && navigate(routes.login);
        setError(data.error);
      });
    }
  }, []);

  return (
    <Row className={`me-0 ms-0 ${styles.container}`}>
      <Col className={styles.image}>
        <Image src={errorImage} />
      </Col>
      <Col className={styles.separator} />
      <Col className={styles.errorContainer}>
        <Row className="flex-column">
          <Col className={styles.status}>{error?.status ?? 'Error'}</Col>
          {oauthError && <Col className={styles.message}>{oauthMessage(oauthError)}</Col>}
          <Col className={styles.code}>{error?.code}</Col>
          <Col className={styles.message}>
            {isServerError(error?.code) ? GENERIC_SERVER_ERROR : error?.message}
          </Col>
          <Col className="pt-3">
            <Link to={(referrer) ? referrer : routes.home}>
              <Button className={styles.button}>Take me home</Button>
            </Link>
          </Col>
        </Row>
      </Col>
    </Row>
  );
});
